import { useEffect, useState } from "react";
import { Redirect } from "wouter";
import { getMerchantToken, merchantFetch } from "../lib/merchantAuth";
import { MerchantNav } from "../components/MerchantNav";
import { InboxMarkdown } from "../components/InboxMarkdown";

/**
 * /merchant/inbox — Phase 15 merchant composer.
 *
 * Today's only recipient filter is ALL_ENROLLED. The merchant types
 * subject + body, picks a category (TRANSACTIONAL or MARKETING), and
 * optionally attaches a single CTA (label + safe URL). The composer
 * shows a live preview using the same InboxMarkdown sanitizer the
 * customer-side detail screen uses, so what the merchant sees IS what
 * the customer renders.
 *
 * Server-side guard rails the merchant cannot bypass (see D47):
 *   • Marketing messages skip customers who have opted out via
 *     `marketingOptInAt = NULL` on tenant_customers.
 *   • Per-recipient cap of TENANT_INBOX_RATE_LIMIT_PER_DAY (=3) sends
 *     in any rolling 24h window (`tenant_inbox_send_log`); applied to
 *     BOTH MARKETING and TRANSACTIONAL sends.
 *   • Subject ≤ 100, body ≤ 2000, ctaUrl SafeUrlSchema-valid.
 *
 * The send-result panel surfaces `sent / skippedMarketingOptOut /
 * skippedRateLimit / skippedNotEnrolled` so the merchant can see WHY
 * fewer customers received the message than they expected.
 */

type Channel = "INBOX" | "EMAIL" | "SMS";

interface ChannelCounts {
  attempted: number;
  success: number;
  skippedMarketingOptOut: number;
  skippedRateLimit: number;
  skippedNoChannel: number;
  skippedNotConfigured: number;
  failed: number;
}

interface ComposeResponse {
  sent: number;
  skippedMarketingOptOut: number;
  skippedRateLimit: number;
  skippedNotEnrolled: number;
  channels?: {
    inbox: ChannelCounts;
    email: ChannelCounts;
    sms: ChannelCounts;
  };
  smsCreditsCharged?: number;
  smsCreditsRemaining?: number;
}

interface CreditsBalance {
  balance: number;
  lifetimePurchased: number;
  lifetimeConsumed: number;
  updatedAt: string;
}

interface SentRow {
  id: string;
  subject: string;
  category: "TRANSACTIONAL" | "MARKETING";
  recipientCount: number;
  sentAt: string;
}

interface SentList {
  items: SentRow[];
  nextCursor: string | null;
}

const SUBJECT_MAX = 100;
const BODY_MAX = 2000;
const CTA_LABEL_MAX = 40;

export function MerchantInboxPage() {
  if (!getMerchantToken()) return <Redirect to="/merchant/login" />;
  return <MerchantInboxInner />;
}

function MerchantInboxInner() {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState<"TRANSACTIONAL" | "MARKETING">(
    "MARKETING",
  );
  const [ctaLabel, setCtaLabel] = useState("");
  const [ctaUrl, setCtaUrl] = useState("");
  const [channels, setChannels] = useState<Set<Channel>>(
    () => new Set(["INBOX"]),
  );

  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState<string | null>(null);
  const [result, setResult] = useState<ComposeResponse | null>(null);

  const [sent, setSent] = useState<SentRow[] | null>(null);
  const [sentErr, setSentErr] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const [credits, setCredits] = useState<CreditsBalance | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const r = await merchantFetch<CreditsBalance>(
        "/api/v1/tenant/me/sms-credits",
      );
      if (cancelled) return;
      if (r.ok) setCredits(r.data);
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  function toggleChannel(c: Channel) {
    setChannels((prev) => {
      const next = new Set(prev);
      if (next.has(c)) {
        if (next.size > 1) next.delete(c);
      } else {
        next.add(c);
      }
      return next;
    });
  }

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setSent(null);
      setSentErr(null);
      const r = await merchantFetch<SentList>(
        "/api/v1/tenant/me/inbox/sent?limit=10",
      );
      if (cancelled) return;
      if (!r.ok) setSentErr(r.message);
      else setSent(r.data.items);
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  function validateAndBuild():
    | {
        subject: string;
        body: string;
        category: "TRANSACTIONAL" | "MARKETING";
        recipientFilter: { type: "ALL_ENROLLED" };
        channels: Channel[];
        ctaLabel?: string;
        ctaUrl?: string;
      }
    | string {
    const s = subject.trim();
    const b = body.trim();
    if (s.length === 0) return "Subject is required.";
    if (s.length > SUBJECT_MAX) return `Subject must be ≤ ${SUBJECT_MAX} chars.`;
    if (b.length === 0) return "Message body is required.";
    if (b.length > BODY_MAX) return `Body must be ≤ ${BODY_MAX} chars.`;
    const cl = ctaLabel.trim();
    const cu = ctaUrl.trim();
    if ((cl.length > 0) !== (cu.length > 0)) {
      return "Provide both a CTA label and CTA URL, or neither.";
    }
    if (cu.length > 0 && !/^https?:\/\//i.test(cu)) {
      return "CTA URL must start with http:// or https://.";
    }
    if (cl.length > CTA_LABEL_MAX) {
      return `CTA label must be ≤ ${CTA_LABEL_MAX} chars.`;
    }
    if (channels.size === 0) return "Pick at least one channel.";
    return {
      subject: s,
      body: b,
      category,
      recipientFilter: { type: "ALL_ENROLLED" },
      channels: Array.from(channels),
      ...(cl.length > 0
        ? { ctaLabel: cl, ctaUrl: cu }
        : {}),
    };
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitErr(null);
    setResult(null);
    const built = validateAndBuild();
    if (typeof built === "string") {
      setSubmitErr(built);
      return;
    }
    setSubmitting(true);
    try {
      const r = await merchantFetch<ComposeResponse>(
        "/api/v1/tenant/me/inbox/send",
        { method: "POST", body: JSON.stringify(built) },
      );
      if (!r.ok) {
        setSubmitErr(r.message);
        return;
      }
      setResult(r.data);
      setSubject("");
      setBody("");
      setCtaLabel("");
      setCtaUrl("");
      setRefreshKey((k) => k + 1);
    } finally {
      setSubmitting(false);
    }
  }

  const previewBody = body.trim();

  return (
    <div className="min-h-screen pb-12">
      <div className="mx-auto max-w-2xl px-6 pt-6">
        <MerchantNav current="inbox" />

        <h1 className="mt-6 text-2xl font-bold text-navy">Send a message</h1>
        <p className="mt-1 text-sm text-ink-dark">
          Reach all enrolled customers via their in-app inbox. Marketing
          messages skip customers who turned off your marketing toggle.
          Any single customer won't receive more than 3 messages from you
          in a rolling 24-hour window (this cap covers both marketing and
          transactional sends).
        </p>

        <form onSubmit={onSubmit} className="card mt-6 space-y-4" noValidate>
          <Field
            label={`Subject (${subject.length}/${SUBJECT_MAX})`}
            htmlFor="subject"
          >
            <input
              id="subject"
              data-testid="input-subject"
              type="text"
              maxLength={SUBJECT_MAX}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="input"
              placeholder="Holiday hours update"
            />
          </Field>

          <Field
            label={`Message (${body.length}/${BODY_MAX}) — supports **bold**, *italic*, [links](https://…)`}
            htmlFor="body"
          >
            <textarea
              id="body"
              data-testid="input-body"
              maxLength={BODY_MAX}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="input min-h-[140px]"
              placeholder="Hi! We'll be closed on **Dec 25** and reopen on Dec 26."
            />
          </Field>

          <Field label="Category" htmlFor="category">
            <select
              id="category"
              data-testid="select-category"
              value={category}
              onChange={(e) =>
                setCategory(e.target.value as "TRANSACTIONAL" | "MARKETING")
              }
              className="input"
            >
              <option value="MARKETING">Marketing — promotions / news</option>
              <option value="TRANSACTIONAL">
                Transactional — important account info
              </option>
            </select>
            <p className="mt-1 text-xs text-ink-dark/70">
              Transactional bypasses the marketing opt-out (so it reaches
              customers who muted your marketing), but it still respects
              the 3-per-recipient-per-24h cap. Use sparingly — only for
              things like store closures or fulfilment updates.
            </p>
          </Field>

          <fieldset className="rounded-card border border-navy/10 bg-cream/30 p-3">
            <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-navy/70">
              Send via
            </legend>
            <div className="flex flex-wrap gap-4 text-sm text-ink-dark">
              {(["INBOX", "EMAIL", "SMS"] as const).map((c) => (
                <label
                  key={c}
                  className="flex items-center gap-2"
                  data-testid={`label-channel-${c.toLowerCase()}`}
                >
                  <input
                    type="checkbox"
                    data-testid={`checkbox-channel-${c.toLowerCase()}`}
                    checked={channels.has(c)}
                    onChange={() => toggleChannel(c)}
                  />
                  <span>
                    {c === "INBOX"
                      ? "In-app inbox"
                      : c === "EMAIL"
                        ? "Email"
                        : "SMS"}
                  </span>
                  {c === "SMS" && credits && (
                    <span
                      className="ml-1 rounded-full bg-navy/10 px-2 py-0.5 text-[11px] font-semibold text-navy"
                      data-testid="badge-sms-credits"
                    >
                      {credits.balance} credit{credits.balance === 1 ? "" : "s"}
                    </span>
                  )}
                </label>
              ))}
            </div>
            <p className="mt-2 text-xs text-ink-dark/70">
              Per-recipient daily caps: 3 inbox, 2 email, 1 SMS. SMS
              consumes one credit per recipient and is prefixed with your
              business name.
            </p>
          </fieldset>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label="CTA label (optional)" htmlFor="cta-label">
              <input
                id="cta-label"
                data-testid="input-cta-label"
                type="text"
                maxLength={CTA_LABEL_MAX}
                value={ctaLabel}
                onChange={(e) => setCtaLabel(e.target.value)}
                className="input"
                placeholder="Book a table"
              />
            </Field>
            <Field label="CTA URL (optional)" htmlFor="cta-url">
              <input
                id="cta-url"
                data-testid="input-cta-url"
                type="url"
                value={ctaUrl}
                onChange={(e) => setCtaUrl(e.target.value)}
                className="input"
                placeholder="https://yourstore.example.com/book"
              />
            </Field>
          </div>

          {previewBody.length > 0 && (
            <div
              className="rounded-card border border-navy/10 bg-cream/40 p-4"
              data-testid="preview-pane"
            >
              <p className="text-[11px] font-semibold uppercase tracking-wide text-navy/60">
                Preview
              </p>
              <h3 className="mt-1 text-base font-bold text-navy">
                {subject.trim() || "Untitled"}
              </h3>
              <div className="mt-3">
                <InboxMarkdown source={previewBody} />
              </div>
              {ctaLabel.trim() && ctaUrl.trim() && (
                <div className="mt-4">
                  <span className="inline-block rounded-card bg-navy px-4 py-2 text-sm font-semibold text-white">
                    {ctaLabel.trim()}
                  </span>
                </div>
              )}
            </div>
          )}

          {submitErr && (
            <div
              className="rounded-card border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
              role="alert"
              data-testid="text-submit-error"
            >
              {submitErr}
            </div>
          )}

          {result && (
            <div
              className="rounded-card border border-emerald-200 bg-emerald-50 px-3 py-3 text-sm text-emerald-900"
              data-testid="text-submit-result"
            >
              <p className="font-semibold">
                Inbox: {result.channels?.inbox.success ?? result.sent} ·
                Email: {result.channels?.email.success ?? 0} · SMS:{" "}
                {result.channels?.sms.success ?? 0}
              </p>
              {typeof result.smsCreditsCharged === "number" &&
                result.smsCreditsCharged > 0 && (
                  <p
                    className="mt-1 text-xs text-emerald-900/80"
                    data-testid="text-sms-credits-charged"
                  >
                    Charged {result.smsCreditsCharged} SMS credit
                    {result.smsCreditsCharged === 1 ? "" : "s"} ·{" "}
                    {result.smsCreditsRemaining} remaining.
                  </p>
                )}
              {(result.skippedMarketingOptOut > 0 ||
                result.skippedRateLimit > 0 ||
                result.skippedNotEnrolled > 0 ||
                (result.channels?.email.failed ?? 0) > 0 ||
                (result.channels?.sms.failed ?? 0) > 0) && (
                <ul className="mt-1 list-inside list-disc text-xs text-emerald-900/80">
                  {result.skippedMarketingOptOut > 0 && (
                    <li>
                      Skipped {result.skippedMarketingOptOut} for marketing
                      opt-out.
                    </li>
                  )}
                  {result.skippedRateLimit > 0 && (
                    <li>
                      Skipped {result.skippedRateLimit} due to the
                      per-channel daily cap.
                    </li>
                  )}
                  {(result.channels?.email.skippedNoChannel ?? 0) > 0 && (
                    <li>
                      Skipped {result.channels!.email.skippedNoChannel} email
                      send(s) — no email on file.
                    </li>
                  )}
                  {(result.channels?.sms.skippedNoChannel ?? 0) > 0 && (
                    <li>
                      Skipped {result.channels!.sms.skippedNoChannel} SMS
                      send(s) — no phone on file.
                    </li>
                  )}
                  {(result.channels?.email.failed ?? 0) > 0 && (
                    <li>{result.channels!.email.failed} email send(s) failed.</li>
                  )}
                  {(result.channels?.sms.failed ?? 0) > 0 && (
                    <li>
                      {result.channels!.sms.failed} SMS send(s) failed
                      (credits refunded).
                    </li>
                  )}
                </ul>
              )}
            </div>
          )}

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary"
              data-testid="button-send"
            >
              {submitting ? "Sending…" : "Send message"}
            </button>
          </div>
        </form>

        <h2 className="mt-10 text-lg font-semibold text-navy">Recently sent</h2>
        {sentErr && (
          <div className="mt-3 rounded-card border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {sentErr}
          </div>
        )}
        {sent === null ? (
          <p className="mt-2 text-sm text-ink-dark">Loading…</p>
        ) : sent.length === 0 ? (
          <p className="mt-2 text-sm text-ink-dark">
            You haven't sent any messages yet.
          </p>
        ) : (
          <ul
            className="mt-3 space-y-2"
            aria-label="Sent messages"
            data-testid="list-sent"
          >
            {sent.map((row) => (
              <li
                key={row.id}
                className="card flex items-center justify-between"
                data-testid={`row-sent-${row.id}`}
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-navy">
                    {row.subject}
                  </p>
                  <p className="mt-0.5 text-xs text-ink-dark">
                    {row.category} · {row.recipientCount} recipient
                    {row.recipientCount === 1 ? "" : "s"} ·{" "}
                    {new Date(row.sentAt).toLocaleString()}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Field(props: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <label htmlFor={props.htmlFor} className="block">
      <span className="block text-xs font-semibold uppercase tracking-wide text-navy/70">
        {props.label}
      </span>
      <div className="mt-1">{props.children}</div>
    </label>
  );
}
